/**
 * Shared constants for the notifications admin module.
 *
 * Values mirror the backend's `communicator/audience.py` and
 * `communicator/models.py::NotificationConfig` enums. If a value drifts,
 * the backend is canonical — copy the change here, not the other way.
 */

// ---- audience types ----------------------------------------------------

export const AUDIENCE_TYPES = ['single_agent', 'all', 'agents', 'customers', 'admins'] as const;
export type AudienceType = (typeof AUDIENCE_TYPES)[number];

// ---- agent types (sub-filter, only meaningful for audience_type='agents')

// `direct_agent` rolls up NULL + empty `account_type` rows server-side
// (~60% of active affiliates) so admins don't have to know the data quirk.
export const AGENT_TYPES = [
  'mlm_agent',
  'direct_agent',
  'subagent',
  'inspection_garage',
  'ao_agent',
  'business_manager',
  'dealership',
  'telesales',
] as const;
export type AgentType = (typeof AGENT_TYPES)[number];

// ---- channels ----------------------------------------------------------

// Only channels with a wired backend handler. Mobile FCM (`push`) and
// `slack` are intentionally absent — `push` is the mobile-app side
// (handled by `BasePushNotification`, not broadcast-ready); `slack` has
// no broadcast use case today.
export const CHANNELS = ['in_app', 'web_push', 'email', 'sms', 'line'] as const;
export type Channel = (typeof CHANNELS)[number];

// ---- config-level audience buckets -------------------------------------

// Values match `NotificationConfig.AUDIENCE_CHOICES` on the backend.
// Used by the configs form's target_audiences multi-select. NOTE: this
// is a SEPARATE vocabulary from the broadcast endpoint's `audience_type`
// (which routes recipients) — `target_audiences` is documentation
// metadata only; dispatch ignores it.
export const CONFIG_TARGET_AUDIENCES = ['agents', 'customers', 'external', 'admin', 'insurer'] as const;
export type ConfigTargetAudience = (typeof CONFIG_TARGET_AUDIENCES)[number];

// ---- priority ----------------------------------------------------------

// Matches `NotificationConfig.PRIORITY_*` on the backend (1=critical .. 5=lowest).
export const PRIORITY_CHOICES = [
  { value: 1, label: 'Critical' },
  { value: 2, label: 'High' },
  { value: 3, label: 'Medium' },
  { value: 4, label: 'Low' },
  { value: 5, label: 'Lowest' },
] as const;
export const DEFAULT_PRIORITY = 3;

// ---- subcategory tabs --------------------------------------------------

// Closed seed set for now — backend stores `subcategory` as a free string so
// ops can introduce new buckets, but the UI only surfaces these three until
// a tab definition is added here. Both the admin list and the tray import
// from this constant so the two strips stay in lockstep.
export const INBOX_TABS = ['general', 'assignment', 'mention'] as const;
export type InboxTab = (typeof INBOX_TABS)[number];

export const INBOX_DEFAULT_TAB: InboxTab = 'general';
